import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../config/paths";
import { aiDataMappingHref } from "../../OrganizationQuestionnaire/aiIngestTarget";
import { useAiIngestRuns } from "../../../hooks/query/questionnaire";
import { commitsBySessionId } from "../../../utils/aiIngestTargets";
import { createSession, deleteSession, listSessions } from "../api/dataMappingClient";
import { isEmptySession, SessionPicker } from "./SessionPicker";

// The overlay shown from a data task's "AI Mapping" button. Scoped to session pick/create/delete only —
// once a session is chosen it's a plain navigation into the real workspace (`SessionLayout`), which owns
// the whole viewport (resizable split pane, collapsed sidebar) and can't live inside a modal.
export default function AiMappingModal({ isOpen, onClose, questionnaireId, target }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("loading"); // loading | picker
  const [sessions, setSessions] = useState([]);
  const [totalSessionCount, setTotalSessionCount] = useState(0);
  const [maxSessions, setMaxSessions] = useState(10);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Whether a session actually wrote data into the task lives on the Java-owned ai_ingest_run record, not
  // on the session itself (its own status is just the agent's workflow stage) — so this cross-references
  // the task's ingest history by `sessionId` rather than trusting anything on the session object.
  const { data: ingestRuns } = useAiIngestRuns(questionnaireId);
  const commitsBySession = useMemo(() => commitsBySessionId(ingestRuns), [ingestRuns]);

  const goToSession = useCallback(
    (sessionId) => {
      onClose();
      navigate(aiDataMappingHref(routes.aiDataMapping, questionnaireId, target, sessionId));
    },
    [navigate, onClose, questionnaireId, target],
  );

  const startNewSession = useCallback(async () => {
    setIsCreating(true);
    setCreateError(null);
    try {
      const s = await createSession(questionnaireId, target);
      goToSession(s.id);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      setCreateError(
        status === 409 && detail
          ? detail
          : "Couldn't start a new session. Try again or delete an existing one.",
      );
      setIsCreating(false);
    }
  }, [questionnaireId, target, goToSession]);

  const handleDelete = useCallback(async (id) => {
    await deleteSession(id);
    setSessions((prev) => prev.filter((s) => s.id !== id));
    setTotalSessionCount((prev) => Math.max(0, prev - 1));
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setPhase("loading");
    setCreateError(null);

    (async () => {
      try {
        const data = await listSessions(questionnaireId, target);
        if (cancelled) return;
        setMaxSessions(data.max_sessions ?? 10);
        const all = data.sessions || [];
        setTotalSessionCount(all.length);
        // Untouched sessions (no file, agent never ran) are noise on a "resume where you left off" list —
        // nothing to resume. Hide them, and if that leaves nothing worth showing, skip the picker: reuse
        // the most recently opened empty one rather than creating yet another, or create one if there are
        // truly none.
        const meaningful = all.filter((s) => !isEmptySession(s));
        if (meaningful.length > 0) {
          setSessions(meaningful);
          setPhase("picker");
        } else if (all.length > 0) {
          const mostRecent = [...all].sort(
            (a, b) => (b.last_accessed_at || 0) - (a.last_accessed_at || 0),
          )[0];
          goToSession(mostRecent.id);
        } else {
          await startNewSession();
        }
      } catch (err) {
        if (cancelled) return;
        setCreateError(
          err?.response?.status === 401
            ? "Your sign-in has expired. Reload the page to continue."
            : "Couldn't load your sessions for this task. Try reloading the page.",
        );
        setPhase("picker");
      }
    })();

    return () => {
      cancelled = true;
    };
    // startNewSession is stable for a given questionnaireId/target, and re-running this on isOpen alone
    // (not every render) is the point — it's a fresh bootstrap each time the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, questionnaireId, target]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent
        borderRadius="lg"
        maxW="640px"
        w="calc(100vw - 32px)"
        maxH="calc(100vh - 64px)"
        display="flex"
        flexDirection="column"
      >
        <ModalCloseButton />
        <ModalBody pt={8} pb={6} overflowY="auto" overflowX="hidden" flex="1" minH={0}>
          <Text fontSize="20px" fontWeight="700" color="gray.700" mb={4}>
            Continue where you left off, or start a new session.
          </Text>
          {phase === "loading" ? (
            <Box display="flex" alignItems="center" justifyContent="center" py={10}>
              <Spinner size="md" mr={3} />
              <Text color="gray.600">Loading...</Text>
            </Box>
          ) : (
            <SessionPicker
              sessions={sessions}
              totalCount={totalSessionCount}
              maxSessions={maxSessions}
              onContinue={(s) => goToSession(s.id)}
              onDelete={handleDelete}
              onNew={startNewSession}
              isCreating={isCreating}
              createError={createError}
              showInfoBanner={false}
              commitsBySessionId={commitsBySession}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
